import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updateDisplayPrimaryCharts,
  updateInputIntervalsPrimary,
  updateRealtyOffersPrimary,
  clearFiltersPrimary,
  updateDisplayBigNumbersOffersPrimary,
} from './redux/offersPrimary.actions';

import {
  handleInputsIntervals,
  handlePriceInputs,
  validateForm,
} from './redux/offersPrimary.controller';

import {
  setDisplayCharts,
  updateClusters,
  updateMarkers,
} from '../redux/dashboardActions';
import {
  PrimaryMarketInputs,
  NameIdPair,
  ConstructionPatternFilter,
  Map,
  Eco,
  MultiSelectFilterEvent,
} from '../types';

import {
  Button,
  Input,
  IResponse,
  Select,
  SelectCalendar,
  SelectSub,
  MenuWrapper,
} from '../../../component';

import './realtyOffers.scss';
import {
  setMaxDate,
  setMaxUsefulArea,
  setMinDate,
  setMinMaxAreaLocalFilters,
  setMinMaxDateLocalFilters,
  setMinUsefulArea,
} from '../charts/redux/charts.actions';

type RealtyOffersPrimaryProps = {
  closeModalAction?: () => void;
};

const RealtyOffersPrimary: React.FC<RealtyOffersPrimaryProps> = ({
  closeModalAction,
}: RealtyOffersPrimaryProps) => {
  const dispatch = useDispatch();
  const dataAtual = new Date();

  const [validation, setValidation]: any = useState({});
  const [filteredConstructionPattern, setFilteredConstructionPattern] =
    useState<NameIdPair[]>([]);
  const [filteredStatus, setFilteredStatus] = useState<NameIdPair[]>([]);

  const { moreFilters } = useSelector((state: any) => state.moreFiltersState);
  const { preference } = useSelector((state: any) => state.layoutState);
  const { minMaxArea, minMaxDate } = useSelector(
    (state: any) => state.chartsState.localFilters
  );

  const nls = require(`./nls/${preference.language.id}.json`);

  const { realtyOffersFiltersOptions, realtyOffersPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );

  const { markers, clusters } = useSelector(
    (state: any) => state.dashboardState
  );

  const inputPatterns: PrimaryMarketInputs = {
    timeframemin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.timeframemin === 'biggerThanMax' ? nls.minBiggerThanMax : '',
    },
    timeframemax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.timeframemax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    privateAreaIntervalmin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.privateAreaIntervalmin === 'biggerThanMax'
          ? nls.minBiggerThanMax
          : '',
    },
    privateAreaIntervalmax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.privateAreaIntervalmax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    unitPricemin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.unitPricemin === 'biggerThanMax' ? nls.minBiggerThanMax : '',
    },
    unitPricemax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.unitPricemax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    pricePerSquareMtmin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.pricePerSquareMtmin === 'biggerThanMax'
          ? nls.minBiggerThanMax
          : '',
    },
    pricePerSquareMtmax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.pricePerSquareMtmax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
  };

  const {
    bedrooms,
    suites,
    garages,
    selectedConstructionPattern,
    selectedStatus,
    inputIntervals,
  } = realtyOffersPrimary;
  const {
    constructionPattern,
    status,
    buildings,
    companies,
    realtyTypes,
    launchingYears,
  } = realtyOffersFiltersOptions;
  const [previousStateConstrPattern, setPreviousStateConstrPattern] = useState<
    ConstructionPatternFilter<Eco | Map>[]
  >(selectedConstructionPattern);
  const mapSubItems: ConstructionPatternFilter<Map>[] = [
    { name: 'MÉDIO', id: 'MÉDIO', parent: false },
    { name: 'MÉDIO ALTO', id: 'MÉDIO ALTO', parent: false },
    { name: 'ALTO PADRÃO', id: 'ALTO PADRÃO', parent: false },
    { name: 'LUXO', id: 'LUXO', parent: false },
  ];
  const ecoSubItems: ConstructionPatternFilter<Eco>[] = [
    { name: 'ECONÔMICO', id: 'ECONÔMICO', parent: false },
    { name: 'CVA', id: 'CVA', parent: false },
  ];
  const eco: ConstructionPatternFilter<Eco> = {
    name: 'ECO',
    id: 'ECO',
    parent: false,
  };
  const map: ConstructionPatternFilter<Map> = {
    name: 'MAP',
    id: 'MAP',
    parent: false,
  };
  useEffect(() => {
    setFilteredConstructionPattern(constructionPattern);
    setFilteredStatus(status);
  }, [buildings, companies]);

  const shouldRemoveSubItems = (
    parent: ConstructionPatternFilter<Eco | Map>,
    previousState: ConstructionPatternFilter<Eco | Map>[],
    currentState: ConstructionPatternFilter<Eco | Map>[],
    subItems: ConstructionPatternFilter<Eco | Map>[]
  ): boolean => {
    const previousStateIncludesParent = previousState.some(
      (el) => el.id === parent.id
    );
    const currentStateIncludesParent = currentState.some(
      (el) => el.id === parent.id
    );
    const currentStateIsNotEmpty = currentState.length > 0;
    const currentStateIncludesAllSubItems = (
      arr: ConstructionPatternFilter<Eco | Map>[],
      target: ConstructionPatternFilter<Eco | Map>[]
    ) => target.every((el) => arr.some((elem) => el.id === elem.id));
    return (
      currentState &&
      currentStateIsNotEmpty &&
      currentStateIncludesAllSubItems(currentState, subItems) &&
      previousStateIncludesParent &&
      !currentStateIncludesParent
    );
  };

  const checkForSideEffects = (
    eventValue: ConstructionPatternFilter<Eco | Map>[]
  ) => {
    if (shouldAddAllSubItems('ECO', eventValue)) {
      return addAllSubItems(eventValue, ecoSubItems);
    }
    if (shouldAddAllSubItems('MAP', eventValue)) {
      return addAllSubItems(eventValue, mapSubItems);
    }
    if (shouldRemoveParent('ECO', eventValue, ecoSubItems)) {
      return eventValue.filter((el) => el.id !== 'ECO');
    }
    if (shouldRemoveParent('MAP', eventValue, mapSubItems)) {
      return eventValue.filter((el) => el.id !== 'MAP');
    }
    if (shouldAddParent('ECO', eventValue, ecoSubItems)) {
      return addParent(eco, eventValue);
    }
    if (shouldAddParent('MAP', eventValue, mapSubItems)) {
      return addParent(map, eventValue);
    }
    if (
      shouldRemoveSubItems(
        eco,
        previousStateConstrPattern,
        eventValue,
        ecoSubItems
      )
    ) {
      return removeSubItems(eco, ecoSubItems, eventValue);
    }
    if (
      shouldRemoveSubItems(
        map,
        previousStateConstrPattern,
        eventValue,
        mapSubItems
      )
    ) {
      return removeSubItems(map, mapSubItems, eventValue);
    }
    return eventValue;
  };

  const handleUpdateConstructionPattern = (event: any) => {
    const eventValue = event.value;
    const areAllSelected =
      eventValue.length === filteredConstructionPattern.length;
    if (!areAllSelected) {
      const updatedValue = checkForSideEffects(eventValue);
      buildPayloadAndDispatchConstrPattern(updatedValue);
      return;
    }
    buildPayloadAndDispatchConstrPattern(eventValue);
  };

  const handleUpdateStatus = (e: any) => {
    const payload = {
      name: 'selectedStatus',
      value: e.value,
    };
    dispatch(updateRealtyOffersPrimary(payload));
  };

  const removeSubItems = (
    parent: ConstructionPatternFilter<Eco | Map>,
    subItems: ConstructionPatternFilter<Eco | Map>[],
    currentState: ConstructionPatternFilter<Eco | Map>[]
  ) => {
    const subItemsIds = subItems.map((el) => el.id);
    return currentState.filter(
      (el) => !subItemsIds.includes(el.id) && el.id !== parent.id
    );
  };

  const addParent = (
    parent: ConstructionPatternFilter<Eco | Map>,
    currentState: ConstructionPatternFilter<Eco | Map>[]
  ) => {
    return [...currentState, parent];
  };

  const handleFilterUpdate = (name: any, userInput: any) => {
    if (name === 'constructionPattern' && userInput.length >= 4) {
      const filtered = constructionPattern.filter((el: NameIdPair) =>
        el.name.toLowerCase().includes(userInput.toLowerCase())
      );
      setFilteredConstructionPattern(filtered);
    } else if (name === 'constructionPattern' && userInput.length === 0) {
      setFilteredConstructionPattern(constructionPattern);
    }
    if (name === 'status' && userInput.length > 0) {
      const filtered = status.filter((el: any) =>
        el.name.toLowerCase().includes(userInput.toLowerCase())
      );
      setFilteredStatus(filtered);
    } else if (name === 'status' && userInput.length === 0) {
      setFilteredStatus(status);
    }
  };

  const addAllSubItems = (
    value: ConstructionPatternFilter<Eco | Map>[],
    subItems: ConstructionPatternFilter<Eco | Map>[]
  ): ConstructionPatternFilter<Eco | Map>[] => {
    return [
      ...value,
      ...subItems.map((el: any) => {
        if (
          selectedConstructionPattern.every(
            (elem: ConstructionPatternFilter<Eco | Map>) => elem.id !== el.id
          )
        ) {
          return el;
        }
      }),
    ];
  };

  const shouldAddAllSubItems = (
    parentType: 'ECO' | 'MAP',
    eventValue: ConstructionPatternFilter<Eco | Map>[]
  ): boolean => {
    const isParentTypeInEventValue =
      eventValue.filter((el) => el.id === parentType).length > 0;
    const isParentTypeSelected =
      selectedConstructionPattern.filter(
        (el: ConstructionPatternFilter<Eco | Map>) => el.id === parentType
      ).length !== 0;
    return isParentTypeInEventValue && !isParentTypeSelected;
  };

  const buildPayloadAndDispatchConstrPattern = (
    value: ConstructionPatternFilter<Eco | Map>[]
  ): void => {
    const payload = {
      name: 'selectedConstructionPattern',
      value: value.filter((el) => el !== undefined),
    };
    setPreviousStateConstrPattern(payload.value);
    dispatch(updateRealtyOffersPrimary(payload));
  };

  const shouldAddParent = (
    parent: 'ECO' | 'MAP',
    eventValue: ConstructionPatternFilter<Eco | Map>[],
    subItems: ConstructionPatternFilter<Eco | Map>[]
  ): boolean => {
    const includesAllSubItems = (
      arr: ConstructionPatternFilter<Eco | Map>[],
      target: ConstructionPatternFilter<Eco | Map>[]
    ) => target.every((el) => arr.some((elem) => el.id === elem.id));
    const includesParent =
      eventValue.filter((el) => el.id === parent).length > 0;
    const previousStateIncludesParent = previousStateConstrPattern.some(
      (el) => el.id === parent
    );
    return (
      includesAllSubItems(eventValue, subItems) &&
      !includesParent &&
      !previousStateIncludesParent
    );
  };

  const shouldRemoveParent = (
    parent: 'ECO' | 'MAP',
    eventValue: ConstructionPatternFilter<Eco | Map>[],
    subItems: ConstructionPatternFilter<Eco | Map>[]
  ): boolean => {
    const includesAll = (
      arr: ConstructionPatternFilter<Eco | Map>[],
      target: ConstructionPatternFilter<Eco | Map>[]
    ) => target.every((el) => arr.some((elem) => el.id === elem.id));
    const includesParent =
      eventValue.filter((el) => el.id === parent).length > 0;
    return !includesAll(eventValue, subItems) && includesParent;
  };

  const handleClickMultiSelect = (
    e: any,
    array: Array<any>,
    arrayName: string
  ) => {
    const newArray = array.map((el) =>
      el.name === e.target.name ? { ...el, selected: !el.selected } : el
    );
    const payload = {
      name: arrayName,
      value: newArray,
    };
    dispatch(updateRealtyOffersPrimary(payload));
  };

  const handleClickSelect = (e: any, arrayName: string) => {
    const payload = {
      name: arrayName,
      value: e.value,
    };
    dispatch(updateRealtyOffersPrimary(payload));
  };

  const validateInputs = (
    event: any,
    v: { name: string; message: string },
    tuple: Array<any>,
    key: string
  ) => {
    const replaceCurrency = (el: string) => {
      if (el.includes(',')) return el.replace(',', '.');
      if (el.includes('R$')) {
        return parseInt(el.split('R$')[1].replaceAll('.', ''));
      } else {
        return parseInt(el);
      }
    };
    tuple[1] = {
      min: tuple[1].min.length > 0 ? replaceCurrency(tuple[1].min) : '',
      max: tuple[1].max.length > 0 ? replaceCurrency(tuple[1].max) : '',
    };
    if (Number(tuple[1].min) > Number(tuple[1].max)) {
      setValidation({ ...validation, [event.name]: v.message });
    } else if (!tuple[1].min && !tuple[1].max) {
      setValidation({
        ...validation,
        [`${key}min`]: false,
        [`${key}max`]: false,
      });
    } else {
      setValidation({
        ...validation,
        [`${key}min`]: false,
        [`${key}max`]: false,
      });
    }
  };

  const handleInput = (
    e: any,
    v: IResponse,
    tuple: { min: string; max: string }
  ) => {
    let newMinUsefulArea: string | number = 0;
    let newMaxUsefulArea: string | number = 0;
    if (v.name.includes('privateAreaIntervalmin')) {
      newMinUsefulArea = e.value.length > 0 ? e.value : 0;
      dispatch(
        setMinMaxAreaLocalFilters({ ...minMaxArea, min: newMinUsefulArea })
      );
    } else if (v.name.includes('privateAreaIntervalmax')) {
      newMaxUsefulArea = e.value.length > 0 ? e.value : 0;
      dispatch(
        setMinMaxAreaLocalFilters({ ...minMaxArea, max: newMaxUsefulArea })
      );
    }
    const typeNumber = !e.name.includes('privateAreaInterval') ? 'int' : '';
    if (e.name.toLowerCase().includes('price')) {
      dispatch(handlePriceInputs(e, v, tuple, 'primary'));
    } else {
      dispatch(handleInputsIntervals(e, v, tuple, 'primary', typeNumber));
    }
  };

  const dataValidacao = (e: any) => {
    const val = e.split('-');
    return Number(`${val[1]}${val[0]}`);
  };

  const handleInputCalendar = (e: any, v: any) => {
    const newMinDate = e.value.min.length > 0 ? e.value.min : 0;
    const newMaxDate = e.value.max.length > 0 ? e.value.max : 0;
    if (e.value.min && e.value.max) {
      if (dataValidacao(e.value.min) <= dataValidacao(e.value.max)) {
        setValidation({ ...validation, [e.name]: false });
      } else {
        setValidation({ ...validation, [e.name]: 'Final menor que inicial' });
      }
    } else {
      if (e.value.min === '' && e.value.max === '') {
        setValidation({ ...validation, [e.name]: false });
      } else {
        setValidation({ ...validation, [e.name]: 'Preencha os campos' });
      }
    }
    dispatch([
      setMinMaxDateLocalFilters({ max: newMaxDate, min: newMinDate }),
      updateInputIntervalsPrimary(e),
    ]);
  };

  const handleSubmit = () => {
    if (clusters.activeMap_ !== undefined) {
      markers.forEach((el: any) => {
        el.setMap(null);
      });
      clusters.clearMarkers();
      dispatch([updateMarkers([]), updateClusters([])]);
    }
    dispatch([
      setDisplayCharts(null),
      setMinUsefulArea(minMaxArea.min),
      setMaxUsefulArea(minMaxArea.max),
      setMinDate(minMaxDate.min),
      setMaxDate(minMaxDate.max),
      validateForm(realtyOffersPrimary, validation, 'primary', moreFilters),
      closeModalAction?.(),
    ]);
  };

  const handleClearFilters = () => {
    dispatch([
      setMinMaxAreaLocalFilters({ min: 0, max: 0 }),
      setMinMaxDateLocalFilters({ min: 0, max: 0 }),
      clearFiltersPrimary(),
    ]);
  };

  return (
    <MenuWrapper
      title={nls.primaryMarketMenuTitle}
      market="Primary"
      submitButtonLabel={nls.toApply}
      clearButtonLabel={nls.clearFilters}
      submitAction={handleSubmit}
      clearAction={handleClearFilters}
    >
      <div className="realty-offers-primary-full-container">
        <div className="input-section-container">
          {realtyOffersPrimary.realtyType.id !== ('cs' || 'comm') && (
            <>
              <div className="input-section-container__title">
                <span data-cy={'ContainerTitleRealtyType'}>
                  {nls.constructionPattern}
                </span>
              </div>

              <div className="construction-pattern-checkbox-container row-container">
                {constructionPattern && (
                  <SelectSub
                    style={{ width: '100%' }}
                    cy="ConstructionPattern"
                    disabled={
                      realtyOffersPrimary.realtyType.id === ('cs' || 'comm')
                    }
                    label=""
                    options={filteredConstructionPattern}
                    multiSelect
                    name="constructionPattern"
                    action={(e: MultiSelectFilterEvent) =>
                      handleUpdateConstructionPattern(e)
                    }
                    actionFilter={(e, v) => handleFilterUpdate('status', v)}
                    selected={selectedConstructionPattern || ''}
                    filter
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="input-section-container">
          <div className="input-section-container__title">
            <span data-cy={'ContainerTitleRealtyType'}>{nls.realtyType}</span>
            <span
              data-cy={'ContainerTitleMandatoryRealtyType'}
              className="optional-span"
            >
              {nls.mandatory}
            </span>
          </div>

          <Select
            label=""
            cy="RealtyType"
            options={realtyTypes}
            action={(e: any) => handleClickSelect(e, 'realtyType')}
            selected={realtyOffersPrimary.realtyType || ''}
            name="realtyType"
          />
        </div>
        <div className="input-section-container">
          {Object.entries(inputIntervals).map((el: any, index: number) => (
            <div
              className="timeframe-container row-container"
              key={`input-section-container-${index}`}
            >
              <div className="input-section-container__title">
                <span data-cy={`ContainerTitle${el[0]}`}>
                  {el[0] === 'timeframe' ? nls.timeframePrimary : nls[el[0]]}
                </span>
              </div>

              {el[0] === 'timeframe' ? (
                <div className="calendario-ano">
                  <div className="row-container inputs-container">
                    <section className="calendar-left-selector">
                      <SelectCalendar
                        name={`${el[0]}`}
                        type="yearMonth"
                        placeholder={nls.initial}
                        action={(e: any, v: any) => {
                          handleInputCalendar(
                            { name: e.name, value: { ...el[1], min: e.value } },
                            v
                          );
                        }}
                        selected={el[1].min}
                        dataInicio={`00/${launchingYears[0]}`}
                        dataFim={`${dataAtual.getMonth()}/${dataAtual.getFullYear()}`}
                        cy="min"
                        required={
                          inputPatterns[
                            `${el[0]}min` as keyof PrimaryMarketInputs
                          ]
                        }
                      />
                    </section>
                    <section className="calendar-right-selector">
                      <SelectCalendar
                        name={`${el[0]}`}
                        type="yearMonth"
                        placeholder={nls.final}
                        action={(e: any, v: any) => {
                          handleInputCalendar(
                            { name: e.name, value: { ...el[1], max: e.value } },
                            v
                          );
                        }}
                        selected={el[1].max}
                        dataInicio={`00/${launchingYears[0]}`}
                        dataFim={`${dataAtual.getMonth()}/${dataAtual.getFullYear()}`}
                        cy="max"
                        required={
                          inputPatterns[
                            `${el[0]}max` as keyof PrimaryMarketInputs
                          ]
                        }
                      />
                    </section>
                  </div>
                  {validation?.[el[0]] ? (
                    <span
                      className="campo-obrigatorio-erro"
                      data-cy="MandatorySelectCalendaraFieldSpan"
                    >
                      {validation[el[0]]}
                    </span>
                  ) : null}
                </div>
              ) : (
                <div className="row-container inputs-container">
                  <div>
                    <Input
                      cy={`${el[0]}Min`}
                      action={(e, v) => handleInput(e, v, el[1])}
                      actionBlur={(e, v) =>
                        validateInputs(
                          e,
                          { ...v, message: 'biggerThanMax' },
                          el,
                          el[0]
                        )
                      }
                      name={`${el[0]}min`}
                      placeholder={nls.initial}
                      value={el[1].min}
                      required={
                        inputPatterns[
                          `${el[0]}min` as keyof PrimaryMarketInputs
                        ]
                      }
                      key={`${el[0]}Min`}
                    />
                  </div>
                  <div>
                    <Input
                      cy={`${el[0]}Max`}
                      action={(e, v) => handleInput(e, v, el[1])}
                      actionBlur={(e, v) =>
                        validateInputs(
                          e,
                          { ...v, message: 'smallerThanMin' },
                          el,
                          el[0]
                        )
                      }
                      name={`${el[0]}max`}
                      placeholder={nls.final}
                      value={el[1].max}
                      required={
                        inputPatterns[
                          `${el[0]}max` as keyof PrimaryMarketInputs
                        ]
                      }
                      key={`${el[0]}Max`}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="input-section-container">
            <div className="input-section-container__title">
              <span data-cy="ContainerTitleStatus">{nls.status}</span>
            </div>

            {status && (
              <Select
                style={{ width: '100%' }}
                cy="status"
                label=""
                options={filteredStatus}
                multiSelect
                name="status"
                action={(e) => handleUpdateStatus(e)}
                actionFilter={(e, v) => handleFilterUpdate('status', v)}
                selected={selectedStatus || ''}
                filter
              />
            )}
          </div>
          <div className="input-section-container">
            <div className="buttons-container row-container">
              <span data-cy="ContainerTitleBedrooms">{nls.bedrooms}</span>
              <div className="multiselect-buttons-container row-container">
                {bedrooms.map((el: any, index: number) => (
                  <Button
                    className={`multi-select-button ${
                      el.selected ? 'selected' : ''
                    }`}
                    cy={`BedroomsMultiSelect[${el.name}]`}
                    name={el.name}
                    key={el.name}
                    onClick={(e) =>
                      handleClickMultiSelect(e, bedrooms, 'bedrooms')
                    }
                  >
                    {el.name}
                  </Button>
                ))}
              </div>
            </div>
            <div className="buttons-container row-container">
              <span data-cy="ContainerTitleSuites">{nls.suites}</span>
              <div className="multiselect-buttons-container row-container">
                {suites.map((el: any, index: number) => (
                  <Button
                    className={`multi-select-button  ${
                      el.selected ? 'selected' : ''
                    }`}
                    cy={`SuitesMultiSelect[${el.name}]`}
                    name={el.name}
                    key={el.name}
                    onClick={(e) => handleClickMultiSelect(e, suites, 'suites')}
                  >
                    {el.name}
                  </Button>
                ))}
              </div>
            </div>
            <div className="buttons-container row-container">
              <span data-cy="ContainerTitleGarages">{nls.garages}</span>
              <div className="multiselect-buttons-container row-container">
                {garages.map((el: any, index: number) => (
                  <Button
                    className={`multi-select-button  ${
                      el.selected ? 'selected' : ''
                    }`}
                    cy={`GaragesMultiSelect[${el.name}]`}
                    name={el.name}
                    key={el.name}
                    onClick={(e) =>
                      handleClickMultiSelect(e, garages, 'garages')
                    }
                  >
                    {el.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MenuWrapper>
  );
};

export default RealtyOffersPrimary;
